import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { SignUpRoleIntent, UserStatus } from "Infrastructure/Api/Api";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import { setSessionStorageItem } from "Utils/Storage/SessionStorage/SecureSessionStorage";
import { SessionStorageKeys } from "Utils/Storage/SessionStorage/SessionStorageKeys";

const StyledButton = styled.button`
  cursor: pointer;
  transition: background-color 0.3s, box-shadow 0.3s;
  padding: 15px 16px;
  height: 46px;
  border: none;
  border-radius: 3px;
  box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.04), 0 1px 1px rgba(0, 0, 0, 0.25);
  
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  font-family: "Outfit", sans-serif;
  color: ${({ theme }) => theme.palette.primary.dark};

  background-color: white;

  &:hover {
    box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.25);
  }

  &:active {
    background-color: #eeeeee;
  }

  &:focus {
    outline: none;
    box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.25),
      0 0 0 3px #c8dafc;
  }

  &:disabled {
    filter: grayscale(100%);
    background-color: #ebebeb;
    box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.04), 0 1px 1px rgba(0, 0, 0, 0.25);
    cursor: not-allowed;
  }
`;

const GoogleIcon = styled.div`
  width: 14px;
  height: 14px;
  background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJNMTcuNiA5LjJsLS4xLTEuOEg5djMuNGg0LjhDMTMuNiAxMiAxMyAxMyAxMiAxMy42djIuMmgzYTguOCA4LjggMCAwIDAgMi42LTYuNnoiIGZpbGw9IiM0Mjg1RjQiIGZpbGwtcnVsZT0ibm9uemVybyIvPjxwYXRoIGQ9Ik05IDE4YzIuNCAwIDQuNS0uOCA2LTIuMmwtMy0yLjJhNS40IDUuNCAwIDAgMS04LTIuOUgxVjEzYTkgOSAwIDAgMCA4IDV6IiBmaWxsPSIjMzRBODUzIiBmaWxsLXJ1bGU9Im5vbnplcm8iLz48cGF0aCBkPSJNNCAxMC43YTUuNCA1LjQgMCAwIDEgMC0zLjRWNUgxYTkgOSAwIDAgMCAwIDhsMy0yLjN6IiBmaWxsPSIjRkJCQzA1IiBmaWxsLXJ1bGU9Im5vbnplcm8iLz48cGF0aCBkPSJNOSAzLjZjMS4zIDAgMi41LjQgMy40IDEuM0wxNSAyLjNBOSA5IDAgMCAwIDEgNWwzIDIuNGE1LjQgNS40IDAgMCAxIDUtMy43eiIgZmlsbD0iI0VBNDMzNSIgZmlsbC1ydWxlPSJub256ZXJvIi8+PHBhdGggZD0iTTAgMGgxOHYxOEgweiIvPjwvZz48L3N2Zz4=);
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  flex-shrink: 0;
`;

type Props = {
  isLoading: boolean;
  affiliateCode?: string;
  /**
   * "link": the Google sign-in only proves that the (already signed-in) legacy account owns the
   * Customer Identity with that e-mail. The API hands the code back to the migration page instead
   * of starting a session, so none of the sign-up extras (affiliate, role intent, guest) apply.
   */
  mode?: "sign-in" | "link";
};

// Link mode carries the flag and a per-attempt nonce. The migration page accepts the returned code
// only with the nonce this tab issued, so a code planted in a crafted URL cannot link the caller to
// a foreign account. Encoded as one value on purpose: the sign-in state below joins pairs with a
// bare `&`, which Google reads as the end of `state`.
const buildLinkModeState = () => {
  const nonce = crypto.randomUUID();
  setSessionStorageItem(SessionStorageKeys.GoogleLinkNonce, nonce);
  return encodeURIComponent(`mode=link&nonce=${nonce}`);
};

export const GoogleButton = ({
  isLoading,
  affiliateCode: affiliateCodeProp,
  mode = "sign-in",
}: Props) => {
  const { t } = useResource();
  const user = useLoggedInUser();

  const affiliateCode =
    affiliateCodeProp || getLocalStorageItem(LocalStorageKeys.AffiliateCode);
  // Validated against the known intents (same as AppleButton) — never forward garbage in `state`.
  const storedRoleIntent = getLocalStorageItem(LocalStorageKeys.RoleIntent);
  const roleIntent =
    storedRoleIntent &&
    Object.values(SignUpRoleIntent).includes(
      storedRoleIntent as SignUpRoleIntent,
    )
      ? (storedRoleIntent as SignUpRoleIntent)
      : undefined;
  const isAnonymousUser =
    user?.userStatus === UserStatus.Anonymous && user?.login;
  let signInState = "";

  // Create state parameter that includes both affiliate code and current user login if anonymous
  if (affiliateCode) {
    signInState = `affiliateCode=${encodeURIComponent(affiliateCode)}`;
  }

  // Role registration links are Customer-first — the wanted role rides along as an intent.
  if (roleIntent) {
    const roleIntentParam = `roleIntent=${encodeURIComponent(roleIntent)}`;
    signInState = signInState
      ? `${signInState}&${roleIntentParam}`
      : roleIntentParam;
  }

  if (isAnonymousUser) {
    const anonymousLoginParam = `anonymousLogin=${encodeURIComponent(user.login)}`;
    signInState = signInState
      ? `${signInState}&${anonymousLoginParam}`
      : anonymousLoginParam;
  }

  const scope = import.meta.env.VITE_GOOGLE_SCOPE;
  const redirectUri = import.meta.env.VITE_GOOGLE_REDIRECT_URL;
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  const redirectToGoogle = () => {
    // Built on click so each link attempt gets a fresh nonce.
    const state = mode === "link" ? buildLinkModeState() : signInState;
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&access_type=offline&scope=${scope}&redirect_uri=${redirectUri}&client_id=${clientId}&state=${state}`;
  };

  return (
    <StyledButton
      type="button"
      color="secondary"
      disabled={isLoading}
      onClick={redirectToGoogle}
    >
      <GoogleIcon />
      <span>{t(Resources.Auth.SSO.Google.ButtonLabel)}</span>
    </StyledButton>
  );
};
